import {utils} from '@bsky.app/alf'

export const BLUE_HUE = 211

/**
 * @deprecated use `utils.alpha` from `@bsky.app/alf` instead
 */
export const transparentifyColor = utils.alpha

/**
 * Lighten a hex color by `amount` percentage points of HSL lightness (0-100).
 * If `hex` is not a valid hex color, returns the input unchanged.
 */
export function lighten(hex: string, amount: number): string {
  return adjustLightness(hex, amount)
}

/**
 * Darken a hex color by `amount` percentage points of HSL lightness (0-100).
 * If `hex` is not a valid hex color, returns the input unchanged.
 */
export function darken(hex: string, amount: number): string {
  return adjustLightness(hex, -amount)
}

function adjustLightness(hex: string, delta: number): string {
  try {
    const rgb = hexToRgb(hex)
    if (!rgb) return hex
    const {h, s, l} = rgbToHsl(rgb.r, rgb.g, rgb.b)
    const next = clamp(l + delta, 0, 100)
    const out = hslToRgb(h, s, next)
    return rgbToHex(out.r, out.g, out.b)
  } catch {
    return hex
  }
}

const HEX_PATTERN = /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

export function hexToRgb(
  hex: string,
): {r: number; g: number; b: number} | null {
  try {
    if (typeof hex !== 'string') return null
    const h = hex.startsWith('#') ? hex.slice(1) : hex
    if (!HEX_PATTERN.test(h)) return null
    // 8-digit hex carries an alpha channel we don't use; drop it
    const rgb = h.length === 8 ? h.slice(0, 6) : h
    const expanded =
      rgb.length === 3
        ? rgb
            .split('')
            .map(c => c + c)
            .join('')
        : rgb
    const num = parseInt(expanded, 16)
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    }
  } catch {
    return null
  }
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) => clamp(Math.round(n), 0, 255)
  return `#${((1 << 24) + (c(r) << 16) + (c(g) << 8) + c(b))
    .toString(16)
    .slice(1)}`
}

/**
 * Computes the WCAG contrast ratio between two colors, ranging from 1 (no
 * contrast) to 21 (maximum contrast, i.e. black on white). Returns null if
 * either argument is not a valid hex color.
 *
 * @see https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 */
export function contrastRatio(hexA: string, hexB: string): number | null {
  const rgbA = hexToRgb(hexA)
  const rgbB = hexToRgb(hexB)
  if (!rgbA || !rgbB) return null
  const luminanceA = relativeLuminance(rgbA)
  const luminanceB = relativeLuminance(rgbB)
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Computes the WCAG relative luminance of an RGB color, ranging from 0 (black)
 * to 1 (white).
 *
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function relativeLuminance({
  r,
  g,
  b,
}: {
  r: number
  g: number
  b: number
}): number {
  const toLinear = (channel: number) => {
    const normalized = channel / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

function rgbToHsl(
  r: number,
  g: number,
  b: number,
): {h: number; s: number; l: number} {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return {h: h * 360, s: s * 100, l: l * 100}
}

function hslToRgb(
  h: number,
  s: number,
  l: number,
): {r: number; g: number; b: number} {
  h /= 360
  s /= 100
  l /= 100
  if (s === 0) {
    const v = l * 255
    return {r: v, g: v, b: v}
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const f = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return {
    r: f(h + 1 / 3) * 255,
    g: f(h) * 255,
    b: f(h - 1 / 3) * 255,
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

/**
 * The 13-step primary ramp from `BRAND_PRIMARY` in `src/alf/themes.ts`, kept
 * here purely as a calibration reference so `generateBrandPalette` can derive
 * per-step H/S/L deltas from it. Duplicated (rather than imported) to avoid a
 * circular dependency between this file and `themes.ts`. If `BRAND_PRIMARY`
 * is ever hand-edited, update this copy too.
 */
const CALIBRATION_RAMP = {
  primary_25: '#F7FAFD',
  primary_50: '#E9F2F9',
  primary_100: '#D3E5F4',
  primary_200: '#B4D3EC',
  primary_300: '#89B9E0',
  primary_400: '#5D9FD5',
  primary_500: '#247DC6',
  primary_600: '#1E69A6',
  primary_700: '#185586',
  primary_800: '#13436B',
  primary_900: '#0E2F4B',
  primary_950: '#0A2337',
  primary_975: '#071927',
} as const

type PrimaryRampKey = keyof typeof CALIBRATION_RAMP
type PrimaryRamp = Record<PrimaryRampKey, string>

const RAMP_STEPS = Object.keys(CALIBRATION_RAMP) as PrimaryRampKey[]

type StepDelta = {
  /** Additive hue delta, in degrees, relative to primary_500's hue. */
  hueDelta: number
  /** Multiplicative ratio applied to primary_500's saturation. */
  satRatio: number
  /**
   * Fraction of the lightness "headroom" travelled from primary_500 toward
   * white (positive, for tints) or toward black (negative, for shades).
   * Using headroom as a ratio (rather than a fixed lightness delta) means the
   * ramp degrades gracefully - approaching but never exceeding pure white or
   * black - for base colors much lighter or darker than the calibration blue.
   */
  lightHeadroomRatio: number
}

/**
 * Per-step H/S/L deltas of `CALIBRATION_RAMP`, computed once relative to its
 * own `primary_500`. Reused by `generateBrandPalette` to reproduce the same
 * tint/shade visual character for an arbitrary brand color.
 */
const CALIBRATED_DELTAS: Record<PrimaryRampKey, StepDelta> = (() => {
  const baseRgb = hexToRgb(CALIBRATION_RAMP.primary_500)!
  const base = rgbToHsl(baseRgb.r, baseRgb.g, baseRgb.b)
  const deltas = {} as Record<PrimaryRampKey, StepDelta>
  for (const step of RAMP_STEPS) {
    const rgb = hexToRgb(CALIBRATION_RAMP[step])!
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)
    const hueDelta = hsl.h - base.h
    const satRatio = base.s === 0 ? 1 : hsl.s / base.s
    let lightHeadroomRatio: number
    if (hsl.l >= base.l) {
      const headroom = 100 - base.l
      lightHeadroomRatio = headroom === 0 ? 0 : (hsl.l - base.l) / headroom
    } else {
      const headroom = base.l
      lightHeadroomRatio = headroom === 0 ? 0 : -(base.l - hsl.l) / headroom
    }
    deltas[step] = {hueDelta, satRatio, lightHeadroomRatio}
  }
  return deltas
})()

/**
 * Strictly matches a 6-hex-digit color with no leading `#`, e.g. `DE4C2C`.
 * Used to validate untrusted input (e.g. a `primaryColor` URL query param)
 * before it's ever passed to color generation or persisted storage.
 */
const STRICT_HEX6_PATTERN = /^[0-9a-fA-F]{6}$/

/**
 * Returns true if `value` is a bare 6-hex-digit color (no `#`, no 3/8-digit
 * shorthand or alpha). Intended for validating untrusted external input.
 */
export function isStrictHex6Color(value: string): boolean {
  return typeof value === 'string' && STRICT_HEX6_PATTERN.test(value)
}

/**
 * Generates a 13-step primary color ramp (`primary_25` through `primary_975`)
 * from a single brand color, matching the desaturation/lightening curve of
 * the hand-tuned `BRAND_PRIMARY` ramp in `themes.ts`. The curve is calibrated
 * once (see `CALIBRATED_DELTAS`) from that ramp's own per-step H/S/L deltas
 * relative to its `primary_500`, then reapplied to `baseHex`'s H/S/L so a new
 * brand color produces a ramp with the same visual character rather than a
 * naive linear lightness ramp.
 *
 * Returns `null` if `baseHex` is not a valid hex color.
 */
export function generateBrandPalette(baseHex: string): PrimaryRamp | null {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return null
  const {h, s, l} = rgbToHsl(rgb.r, rgb.g, rgb.b)
  const out = {} as PrimaryRamp
  for (const step of RAMP_STEPS) {
    const d = CALIBRATED_DELTAS[step]
    const newH = (h + d.hueDelta + 360) % 360
    const newS = clamp(s * d.satRatio, 0, 100)
    const newL = clamp(
      d.lightHeadroomRatio >= 0
        ? l + d.lightHeadroomRatio * (100 - l)
        : l + d.lightHeadroomRatio * l,
      0,
      100,
    )
    const {r, g, b} = hslToRgb(newH, newS, newL)
    out[step] = rgbToHex(r, g, b)
  }
  return out
}
